import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(
      ThemeEvents.variantSelected,
      /** @type {EventListener} */ (this.updatePriceOptimistically)
    );
    closestSection.addEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.updatePrice));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(
      ThemeEvents.variantSelected,
      /** @type {EventListener} */ (this.updatePriceOptimistically)
    );
    closestSection.removeEventListener(ThemeEvents.variantUpdate, /** @type {EventListener} */ (this.updatePrice));
  }

  /** @param {VariantSelectedEvent} event */
  updatePriceOptimistically = (event) => {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.dataset.productId !== this.dataset.productId) return;

    const variant = event.detail.resource?.variant;
    if (!variant) return;

    this.#renderVariantPrice(variant);
  };

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }

    // Update installments (SPI banner) variant ID to trigger payment terms re-render
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (event.detail.resource) {
      this.#renderVariantPrice(event.detail.resource);
    }
  };

  /** @param {{ id?: number | string, price?: number, compare_at_price?: number | null }} variant */
  #renderVariantPrice(variant) {
    const priceContainer = this.querySelector('[ref="priceContainer"]') || this.refs.priceContainer;
    const moneyFormat = this.dataset.moneyFormat;
    const currency = this.dataset.currency;

    if (!priceContainer || !moneyFormat || !currency) return;

    const regularPriceContainer = priceContainer.querySelector('.price__regular');
    const salePriceContainer = priceContainer.querySelector('.price__sale');

    if (!regularPriceContainer || !salePriceContainer) return;

    const price = Number(variant.price ?? 0);
    const compareAtPrice = Number(variant.compare_at_price ?? 0);
    const showComparePrice = compareAtPrice > price;
    const discountPercent = showComparePrice ? Math.round(((compareAtPrice - price) * 100) / compareAtPrice) : 0;
    const formattedPrice = formatMoney(price, moneyFormat, currency);
    const formattedCompareAtPrice = showComparePrice ? formatMoney(compareAtPrice, moneyFormat, currency) : '';

    regularPriceContainer.classList.toggle('price__hidden', showComparePrice);
    salePriceContainer.classList.toggle('price__hidden', !showComparePrice);

    const regularPrice = regularPriceContainer.querySelector('.price');
    if (regularPrice) {
      regularPrice.textContent = formattedPrice;
    }

    const salePrice = salePriceContainer.querySelector('.price-item--sale');
    if (salePrice) {
      salePrice.textContent = formattedPrice;
    }

    for (const comparePrice of salePriceContainer.querySelectorAll('.compare-at-price')) {
      comparePrice.textContent = formattedCompareAtPrice;
    }

    const discountBadge = salePriceContainer.querySelector('.price-item__discount-badge');
    if (discountBadge) {
      discountBadge.classList.toggle('hidden', !showComparePrice || discountPercent <= 0);
      discountBadge.textContent = `${discountPercent}% OFF`;
    }
  }

}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
